import * as React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Button, Modal, Icon, Header, Message } from 'semantic-ui-react';
import axios from 'axios';

interface Message {
    mode: (messages: Message[]) => React.ReactNode;
    className?: string;
    priority?: number;
    dimmer?: true | 'blurring' | 'inverted';
    icon?: string;
    title: string;
    policy?: string;
    text: string;
    report?: object | string;

    timestamp?: Date;
    expiresAt?: Date;
}

interface State {
    messages: Message[];
    more: boolean;
    countdown?: number;
}

interface Props {
    className?: string;
    countdown: number;
    debugReport: boolean;
}

const sabrErrorsMap = {
    SUCCESS: 'Success',
    GENERIC_ERROR: 'Error',
    SYNTAX_ERROR: 'Syntax Error',
    INVALID_ARGUMENT: 'Invalid Argument',
    NOT_IMPLEMENTED: 'Not Implemented Yet',
    CLIENT_REJECTED: 'Client Rejected',
    NOT_FOUND: 'Not Found',
    ALREADY_STARTED: 'Already Started',
    ALREADY_STOPPED: 'Already Stopped',
    RESOURCE_BUSY: 'Resource Busy',
    LICENSE_LIMIT: 'License Limit',
    FEATURE_DISABLED: 'Feature Disabled',
    NO_POST_DATA: 'No Data in Request',
    EXPECTED_ARGUMENT: 'Expected Argument',
    INTERNAL_SERVER_ERROR: 'Internal Server Error',
    DUPLICATE: 'Duplicate',
    UNAVAILABLE: 'Resource Unavailable',
    INACTIVE: 'Resource Inactive',
    UNLICENSED: 'Feature Not Licensed',
    MULTIPLE_OBJECTS: 'Multiple Objects/Matches',
    NULL_OBJECT: 'Null Object'
};

export default class AxiosErrorMessage extends React.Component<Props, State> {
    public static defaultProps: Partial<Props> = {
        className: '',
        debugReport: false
    };

    timer: any | undefined;

    public static alias(s: string) {
        return (sabrErrorsMap[s] !== undefined)
            ?  sabrErrorsMap[s]
            : s;
    }

    private static split(messages: Message[]) {
        let report: string|object|undefined = messages.reduce(
            (acc: string, val: Message) =>
                (acc && val && val.report)
                    ? (acc + '\r\n\r\n' + val.report)
                    : ((val && val.report) || undefined),
            undefined  // initial argument
        );
        if (typeof report === 'object')
            report = JSON.stringify(report);
        let priority: Message | undefined = messages.shift();
        return {
            priority,
            other: messages,
            report: report
        };
    }
    private static axiosToReport(axiosRequest: any): string | undefined {
        try {
            let lines: string[] = [];
            let { request, response } = axiosRequest;

            if (response && response.data) {
                let {code, det} = response.data;
                lines.push('HTTP/' + response.status + ' ' + code );
                lines.push(det);
            }
            if (request) {
                lines.push('');
                lines.push(response.config.method.toUpperCase() + '  ' + response.config.url);
                lines.push(response.config.data);
            }
            if (response) {
                lines.push('');
                lines.push('RESPONSE');
                // tslint:disable-next-line:forin
                for (let k in response.headers) {
                    lines.push('    ' + k + ': ' + response.headers[k]);
                }
                lines.push(response.request.responseText);
            }
            return lines.join('\r\n');
        } catch (x) {
            // tslint:disable-next-line:no-console
            console.log('failed to build a report from ', axiosRequest, x);
        }
        return undefined;
    }

    private static ageInSeconds(d: Date): number {
        return Math.floor((new Date().getTime() - d.getTime()) / 1000);
    }

    /*
     *  darkModal - darkens the screen and pops a un-mistakable error
     */
    darkModal(messages: Message[]): React.ReactNode {
        let { priority, other, report } = AxiosErrorMessage.split(messages);
        if (!priority) return false;
        return  (
            <Modal open basic size="small" dimmer={priority.dimmer ? priority.dimmer : true} className={`axiosMessage ${priority.className || ''}`}>
                <Header icon={priority.icon} content={priority.title} />
                <Modal.Content>
                    {priority.policy && <b>{priority.policy}</b>}
                    <p>{priority.text}</p>
                    {(other.length > 0) && this.renderOther(other)}
                </Modal.Content>
                <Modal.Actions>
                    {this.buttons(report)}
                </Modal.Actions>
            </Modal>
        );
    }

    banner(messages: Message[]): React.ReactNode {
        let { priority, other, report } = AxiosErrorMessage.split(messages);
        if (!priority) return false;
        return  (
            <Message negative className={`axiosMessage ${priority.className || ''}`}>
                <Message.Header icon={priority.icon} content={priority.title} />
                {priority.policy && <b>{priority.policy}</b>}
                <p>{priority.text}</p>
                {(other.length > 0) && this.renderOther(other)}
                <Button.Group className="actions" floated="right">
                    {this.buttons(report)}
                </Button.Group>
                <div style={{ clear: 'both' }} />
            </Message>
        );
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            messages: [],
            more: false,
            countdown: this.props.countdown
        };

        this.doClear = this.doClear.bind(this);
        this.revealMore = this.revealMore.bind(this);

        this.darkModal = this.darkModal.bind(this);
        this.banner = this.banner.bind(this);

        axios.interceptors.response.use(
            (response) => {
                // clear errors when we get successful requests (just dont cancel when multiple requests are coming in quick)
                this.setState( prevState => ({ ...prevState, messages: prevState.messages.filter(m => m.timestamp && AxiosErrorMessage.ageInSeconds(m.timestamp) < 5 )}) );
                return response;
            },
            (error) => {
                // only intercept requests with valid URL
                if (error.response && error.response.config && error.response.config.url) {
                    let {data, config, status} = error.response;

                    /// only intercept requests to API
                    let url = new URL(config.url);
                    if (url.pathname.startsWith('/api/')) {
                        let {code, det} = data;
                        let debugReport: boolean = (this.props && this.props.debugReport);

                        // token expired
                        if (status >= 400 && status <= 499) {
                            this.popup({
                                mode: this.banner,
                                priority: status,
                                className: 'bad-request',
                                icon: 'exclamation circle',
                                title: code,
                                policy: data.policy,
                                text: det,
                                report: debugReport ? AxiosErrorMessage.axiosToReport(error) : undefined
                            });
                        } else if (status === 500) {
                            this.popup({
                                mode: this.darkModal,
                                priority: status,
                                className: 'internal-error',
                                icon: 'exclamation circle',
                                title: 'Internal Server Error' + (code ? '  (' + AxiosErrorMessage.alias(code) + ')' : ''),
                                policy: data.policy,
                                text: det,
                                report: debugReport ? AxiosErrorMessage.axiosToReport(error) : undefined
                            });
                        }
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    /*// Used for testing
    sampleInternalError() {
        this.popup({
            mode: this.darkModal,
            priority: 500,
            className: 'internal-error',
            icon: 'exclamation circle',
            title: 'Internal Server Error (' + AxiosErrorMessage.alias('INVALID_ARGUMENT') + ')',
            policy: 'VOD Flags',
            text: 'invalid flags parameter, expected valid time and file modes and related advance fields'
        });
    }

    sampleBadRequest() {
        this.popup({
            mode: this.banner,
            priority: 400,
            className: 'bad-request',
            title: 'EXPECTED_ARGUMENT',
            policy: 'Stream Reference',
            text: 'expected list of streams or stream query expression'
        });
    }

    componentDidMount() {
        this.sampleInternalError();
        this.sampleBadRequest();
    }*/

    public popup(msg: Message) {
        if (!msg.icon)
            msg.icon = 'exclamation triangle';
        msg.title = msg.title
            ? AxiosErrorMessage.alias(msg.title)
            : 'Server Error';
        if (!msg.timestamp)
            msg.timestamp = new Date();
        // we must call async-callback mode of setState because setState() is async, and if too many
        // popup() calls come in too soon then the this.state.messages.concats might clobber one another
        // and lose messages because an earlier setState hasn't finished executing before this.state.messages
        // is referenced in another popup()
        // here all our setState callbacks get serialized by the framework
        this.setState( prevState => ({ ...prevState, messages: prevState.messages.concat(msg)}) );

        if (!this.timer && this.props.countdown) {
            this.timer = setInterval(() => {
                    if (this.state.countdown === 0)
                        this.doClear(null, null);
                    else
                        this.setState({countdown: (this.state.countdown || this.props.countdown ) - 1});
                },
                                     1000);
        }
    }

    public render() {
        let priority = this.getPriority();
        if (!priority) return false;
        let other = this.state.messages.filter( m => m !== priority );
        return (this.state && this.state.messages) ? (
            <div className={'axiosMessages'}>
                {priority.mode([ priority, ...other ])}
            </div>
        ) : null;
    }

    private acknowledgeText() {
        return this.state.countdown
            ? 'Acknowledge (' + this.state.countdown + ')'
            : 'Acknowledge';
    }

    private buttons(report?: string) {
        return (
            <React.Fragment>
                {report &&
                  <CopyToClipboard text={report} onCopy={this.doClear}>
                    <Button basic color="teal" inverted>
                      <Icon name="clipboard outline"/> Copy Report To Clipboard
                    </Button>
                  </CopyToClipboard>
    }
                <Button color="red" inverted onClick={this.doClear}>
                    <Icon name="checkmark" />{this.acknowledgeText()}
                </Button>
            </React.Fragment>
        );
    }

    private revealMore() {
        this.setState({ ...this.state, more: true });
    }

    private doClear(event: any, data: any) {
        this.setState({ messages: [], more: false, countdown: this.props.countdown || AxiosErrorMessage.defaultProps.countdown || 15 });
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }
    }

    private getPriority(): Message | undefined {
        return (this.state.messages && this.state.messages.length > 0)
            ? this.state.messages.reduce((acc, val) => (acc === undefined || (val.priority && acc.priority && (val.priority > acc.priority))) ? val : acc )
            : undefined;
    }

    private renderOther(messages: Message[]) {
        if (messages.length === 0 ) return false;
        if (!this.state.more) {
            return <a onClick={this.revealMore} style={{ cursor: 'pointer' }}>More...</a>;
        } else {
            return (
                <div className="axiosMessage-other">
                    <hr/>
                    {messages.map((m, i) => {
                        return (
                            <div key={i} className="axiosMessage-item">
                                {m.policy && <b>{m.policy ? m.title + ' for ' + m.policy : m.title}</b>}
                                <p>{m.text}</p>
                            </div>
                        );
                    })}
                </div>
            );
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/api/AxiosErrorMessage.tsx